import React from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface PriceTagStyleEditorProps {
  backgroundColor: string;
  textColor: string;
  onBackgroundColorChange: (color: string) => void;
  onTextColorChange: (color: string) => void;
  primaryColor: string;
  secondaryColor: string;
}

/**
 * PriceTagStyleEditor - Simple colour customisation for price tags
 */
const PriceTagStyleEditor: React.FC<PriceTagStyleEditorProps> = ({
  backgroundColor,
  textColor,
  onBackgroundColorChange,
  onTextColorChange,
  primaryColor,
  secondaryColor
}) => {
  const colorPresets = [
    { name: 'Primary', color: primaryColor },
    { name: 'Secondary', color: secondaryColor },
    { name: 'White', color: '#ffffff' },
    { name: 'Black', color: '#2D2D2D' },
    { name: 'Green', color: '#3EA637' },
    { name: 'Blue', color: '#3f92ff' },
    { name: 'Red', color: '#e74c3c' },
    { name: 'Orange', color: '#ff7b42' }
  ];

  const resetToDefaults = () => {
    onBackgroundColorChange(primaryColor);
    onTextColorChange('#ffffff');
  };

  const renderColorPicker = (
    value: string,
    onChange: (color: string) => void,
    label: string
  ) => (
    <div className="space-y-2">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="flex gap-2">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start gap-2">
              <div 
                className="w-4 h-4 rounded border border-gray-300"
                style={{ backgroundColor: value }}
              />
              {value}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64">
            <div className="space-y-3">
              <Input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-10"
              />
              <Input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder="#ffffff"
                className="font-mono text-sm"
              />
              <div className="grid grid-cols-2 gap-1">
                {colorPresets.map((preset) => (
                  <Button
                    key={preset.name}
                    variant="outline"
                    size="sm"
                    onClick={() => onChange(preset.color)}
                    className="justify-start gap-2 h-auto p-2"
                  >
                    <div 
                      className="w-3 h-3 rounded border border-gray-300"
                      style={{ backgroundColor: preset.color }}
                    />
                    <span className="text-xs">{preset.name}</span>
                  </Button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">Price Tag Styling</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {renderColorPicker(backgroundColor, onBackgroundColorChange, 'Background Colour')}
          {renderColorPicker(textColor, onTextColorChange, 'Text Colour')}

          {/* Reset Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={resetToDefaults}
            className="w-full"
          >
            Reset to Defaults
          </Button>
        </div>

        {/* Preview */}
        <div className="mt-6 p-4 bg-gray-100 rounded-md">
          <Label className="text-sm font-medium mb-2 block">Preview</Label>
          <div className="flex gap-3">
            <div 
              className="text-sm font-bold"
              style={{
                backgroundColor: backgroundColor,
                color: textColor,
                padding: '4px 8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minWidth: '60px',
                textAlign: 'center'
              }}
            >
              $12.99
            </div>
            <div 
              className="text-sm font-bold"
              style={{
                backgroundColor: backgroundColor,
                color: textColor,
                padding: '4px 8px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                minWidth: '60px',
                textAlign: 'center'
              }}
            >
              $8.50
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceTagStyleEditor;